export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly reasonCode: string,
    readonly blockers: string[] = [],
    readonly retryAfterSeconds?: number
  ) {
    super(reasonCode);
    this.name = "ApiError";
  }
}

// 会话走同源 Cookie，由 Next 的 /api/[...path] 反代转给 NestJS。
// 浏览器里不保存令牌，也就没有「令牌被脚本读走」这条路。
export async function api<T>(
  path: string,
  options?: { method?: "GET" | "POST"; body?: object }
): Promise<T> {
  const response = await fetch(path, {
    method: options?.method ?? "GET",
    credentials: "same-origin",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(options?.body ? { "Content-Type": "application/json" } : {})
    },
    ...(options?.body ? { body: JSON.stringify(options.body) } : {})
  });
  const data = (await response.json().catch(() => null)) as {
    reasonCode?: string;
    blockers?: unknown;
    retryAfterSeconds?: unknown;
  } | null;
  if (!response.ok) {
    const retryAfter = data?.retryAfterSeconds;
    throw new ApiError(
      response.status,
      data?.reasonCode ?? "REQUEST_REJECTED",
      Array.isArray(data?.blockers)
        ? data.blockers.filter((item): item is string => typeof item === "string")
        : [],
      typeof retryAfter === "number" && Number.isFinite(retryAfter) && retryAfter > 0
        ? Math.ceil(retryAfter)
        : undefined
    );
  }
  return data as T;
}

export function commandId(): string {
  return `web_${crypto.randomUUID().replaceAll("-", "_")}`;
}

export function formatTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

export function formatDate(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium" }).format(new Date(value));
}

// 服务端的拒绝理由要能被读懂。读不懂的原样显示，不编一句安慰话盖过去。
const REASON_TEXT: Record<string, string> = {
  INVALID_TIME_WINDOW: "活动结束时间须晚于开始时间，报名截止时间须晚于报名开始时间",
  INVALID_START_AT: "请填写有效的活动开始时间",
  INVALID_END_AT: "请填写有效的活动结束时间",
  INVALID_ENROLL_OPENS_AT: "请填写有效的报名开始时间",
  INVALID_ENROLL_CLOSES_AT: "请填写有效的报名截止时间",
  INVALID_REASON: "请填写符合要求的办理理由",
  INVALID_REASON_CODE: "请填写有效的理由",
  INVALID_NOTE: "请填写四至五百字的说明",
  INVALID_ROLE: "所选职责不可签发，请核对职责",
  INVALID_SCOPE_ORGANIZATION: "所选组织不在可授范围内，请重新核对",
  INVALID_GRANT_BASIS: "授权依据不符合要求，请重新核对",
  INVALID_VALID_FROM: "请填写有效的生效时间",
  SELF_GRANT_FORBIDDEN: "不能给自己签发授权",
  NOT_REGISTERED: "该会员当前未报名，不能登记到会",
  ALREADY_CHECKED_IN: "该会员已登记到会，请重新读取名册",
  APPOINTMENT_SUPERSEDED: "这条任职已经更正收档，请重新读取记录",
  INVALID_reason: "请填写四至五百字的更正理由",
  RATE_LIMITED: "请求较频繁，请稍后重试",
  PERIOD_TASK_EXISTS: "这一季度已经有统计任务，请刷新后查看",
  PERIOD_NOT_ENDED: "这一季度还没有结束，季度结束后才能发布统计",
  DOMAIN_ROOT_MISSING: "组织树缺少市委会根组织，暂不能发布统计",
  LOGIN_LOCKED: "登录尝试次数较多，请稍后再试",
  UNKNOWN_IMPORT_TARGET: "包含当前不允许导入的字段，请重新核对字段对应",
  DUPLICATE_IMPORT_TARGET: "同一个导入字段只能对应一列",
  MISSING_REQUIRED_IMPORT_TARGET: "请补齐姓名、手机号与所属组织的字段对应",
  MAPPING_NAME_TAKEN: "这个对应规则名称已经使用，请查看已有规则或调整名称",
  MAPPED_COLUMN_NOT_IN_FILE: "文件缺少对应规则要求的表头，请核对文件与规则",
  NO_DATA_ROWS: "文件中没有数据行，请核对表头之后的内容",
  CONTENT_TOO_LARGE: "文件超过 1 MiB，请分批导入",
  TOO_MANY_ROWS: "单批最多 2000 行，请拆分文件",
  BATCH_NOT_STAGED: "该批次已办理，请返回报告读取最新结果",
  INVALID_content: "请先选择有效的 CSV 文件",
  INVALID_columns: "请完整填写源文件表头与导入字段",
  INVALID_CSV: "CSV 引号未闭合，请检查文件格式",
  INVALID_CSV_HEADER: "CSV 表头不能为空或重复，请核对第一行",
  SELF_ACCOUNT_FORBIDDEN: "不能停用当前登录账号",
  STATE_UNCHANGED: "当前记录已处于该状态，请重新读取核对",
  ALREADY_UNBOUND: "此登录方式已解绑，请重新读取",
  NOT_FOUND: "记录不存在，或不在当前授权范围内",
  INVALID_WINDOW: "截止时间须晚于开始时间和当前时间",
  INVALID_REQUIREMENTS: "材料要求最多 12 条，每条不超过 200 字",
  INVALID_REQUIREMENT: "每条材料要求不能超过 200 字",
  INVALID_EXPERTS: "参与专家最多 20 人，每人名称不超过 60 字",
  INVALID_YEAR: "年度须为 2000 至 2200 之间的整数",
  INVALID_CURSOR: "翻页条件已失效，请回到首页重新查询",
  INVALID_REVIEW_RECEIPT: "请填写 4 至 1000 字的会员可见说明",
  INVALID_MERGE_TARGET: "无法归并到该建言，请重新核对目标",
  CLUE_NOT_ELIGIBLE: "来源建言尚未进入后续跟进，请重新核对",
  CLUE_ALREADY_LINKED: "本课题已关联这条建言",
  DISPOSITION_REQUIRED: "结项前请选择后续处置",
  CONTENT_UNCHANGED: "稿件内容与已有版本相同，请核对后再保存",
  MANUSCRIPT_VERSION_MISSING: "选定的稿件版本不存在，请重新读取课题",
  ARCHIVE_BLOCKED: "暂不能归档组织",
  MERGE_BLOCKED: "暂不能合并档案",
  IMPACT_CHANGED: "授权影响已经变化，请重新预览后确认",
  CYCLE_NOT_ALLOWED: "不能将组织移到自身或下级组织之下",
  ORGANIZATION_ARCHIVED: "该组织已归档，无法继续修改",
  MEMBER_ALREADY_EXISTS: "已有同名同手机号的有效档案，请先核对现有档案",
  NOT_AN_ACTIVE_MEMBER: "该会员当前没有有效会籍，不能登记任职",
  TERM_ALREADY_CLOSED: "该届次已经收届，请重新读取",
  TERM_ORDINAL_TAKEN: "该组织已登记这一届数，请核对已有届次",
  CURRENT_TERM_EXISTS: "该组织已有现届，请先完成收届",
  UNKNOWN_DICTIONARY_ITEM: "选项已不可用，请重新读取字典后选择",
  INVALID_primaryCode: "请选择履历类别",
  INVALID_period: "结束日期不能早于开始日期",
  INVALID_CREDENTIALS: "账号或凭据不正确",
  CAPABILITY_NOT_GRANTED: "当前职责不包含这一步",
  NOT_FOUND_OR_DENIED: "内容不存在，或不在你的授权范围内",
  VERSION_CONFLICT: "内容已被他人改动，请重新读取后再试",
  STATE_CHANGED: "状态已经变了，请重新读取",
  NOT_EDITABLE: "只有起草态可以修改；要改先退回重写",
  NOT_DRAFT_AUTHOR: "只有原起草人可以修改这份通知草稿",
  INVALID_CHANNEL: "当前组织域未启用这个栏目，请选择可用栏目",
  AUTHOR_CANNOT_REVIEW: "起草人不能审自己的稿",
  AUTHOR_CANNOT_RELEASE: "起草人不能放行自己的稿",
  REVIEWER_CANNOT_RELEASE: "保密审查人不能同时放行，需要第三个人",
  EMPTY_AUDIENCE: "当前受众为空，不能发给零个人",
  COMMAND_ID_REUSED: "这条命令已经用过了，请重新发起",
  SERVICE_UNAVAILABLE: "服务暂时不可用，请稍后再试",
  UNSUPPORTED_FILE_TYPE: "只接受 PDF、Word、Excel、PNG、JPEG；类型按文件实际内容判定",
  INVALID_FILE_NAME: "文件名与文件实际类型对不上，或含有不允许的字符",
  FILE_TOO_LARGE: "单个附件不超过 10 MB",
  EMPTY_FILE: "空文件不能上传",
  TOO_MANY_ATTACHMENTS: "附件数量已达上限",
  OWNER_NOT_EDITABLE: "只有起草态可以增删附件；要改先退回重写",
  ATTACHMENT_QUARANTINED: "该附件已被隔离，停止分发",
  // 授权与受众（ADR-0030）
  AUDIENCE_NOT_CONTAINED: "受众超出了发起组织的范围。本组织的活动只能发给本组织及其下级",
  AUDIENCE_OUTSIDE_SCOPE: "受众里有你未被授权的组织",
  REAUTH_REQUIRED: "这一步需要二次认证，请再输一次口令",
  REAUTH_CANCELLED: "已取消重认证，本次未提交",
  // 账号激活（ADR-0042）
  ACCOUNT_ALREADY_LINKED: "这个微信账号已绑定了一份档案，请重新读取",
  PERSON_ALREADY_LINKED: "这份档案已有账号绑定，请先到档案里解除绑定",
  MEMBERSHIP_NOT_ACTIVE: "这份档案的会籍不是有效状态，不能绑定",
  NO_EFFECTIVE_LINK: "这份档案当前没有账号绑定，请重新读取",
  INVALID_ID_DOCUMENT_NUMBER: "身份证号格式或校验位不正确，请核对后重新填写",
  ID_DOCUMENT_ALREADY_RECORDED: "这个身份证号已登记在另一份档案上，请先核对是否重复建档",
  ID_DOCUMENT_BIRTH_DATE_MISMATCH: "身份证号里的出生日期与已登记的出生日期不一致，请先核对哪一边有误",
  INVALID_birthDate: "请填写有效的出生日期，且不能晚于今天",
  INVALID_workStartOn: "请填写有效的参加工作时间，且不能晚于今天",
  INVALID_nativePlace: "籍贯不超过 120 字",
  INVALID_householdRegistration: "户籍所在地不超过 120 字",
  INVALID_representativeTier: "中上层只能选 是、否 或留空",
  INVALID_isCentralEnterprise: "是否央企只能选 是、否 或留空",
  INVALID_enterpriseScaleCode: "请选择有效的企业规模",
  INVALID_postingDepartmentTypeCode: "请选择有效的实职安排部门类型",
  INVALID_postingRankCode: "请选择有效的实职安排职级",
  REVIEW_SELF_FORBIDDEN: "签发人不能复核自己签发的授权，需要另一个人",
  ALREADY_REVIEWED: "这条授权已经复核过了",
  ALREADY_REVOKED: "这条授权已经撤销了",
  NO_ACTIVE_GRANT: "你名下没有生效中的授权",
  SCOPE_MUST_EQUAL_TERM_ORGANIZATION: "因职务签发的授权，范围必须正好是该届所属组织，不能多给",
  APPOINTMENT_NOT_HELD: "这条任职已经卸任或收档，不能作为授权依据",
  TERM_CLOSED: "这一届已经收档，不能作为授权依据",
  TERM_NOT_CURRENT_NEEDS_FUTURE_START: "依据的是筹备中的届：可以预置，但生效时间必须在换届之后",
  POST_NOT_LEADING: "这个职务不是领导职务，不能据以签发",
  BASIS_APPOINTMENT_REQUIRED: "因职务签发要指明依据的那条任职",
  NOT_GRANT_HOLDER: "只有持有这条授权的人才能转授",
  DELEGATION_NOT_CHAINABLE: "转授来的授权不能再往下转",
  DELEGATION_OUTSIDE_SCOPE: "转授不能越出你自己的范围",
  DELEGATION_OUTLIVES_UPSTREAM: "转授的到期日不能晚于你自己这条授权",
  DELEGATION_WIDENS_SCOPE: "转授不能把「仅本级」放宽成「含下级」",
  DELEGATE_SELF_FORBIDDEN: "不能把权转授给自己",
  INVALID_VALID_TO: "请核对截止时间：须晚于适用的开始时间，转授或缩短期限须在将来且不能超过原授权期限",
  ACCOUNT_REQUIRED: "先选一个工作台账号——这个人还没有账号，授权无处可落",
  INVALID_AUDIENCE_BASIS: "受众来源只能是「按会籍」或「按现届任职」",
  // 分名额到基层（ADR-0031）
  INVALID_ENROLLMENT_MODE: "报名模式只能是「本人自主报名」或「分名额到基层」",
  QUOTA_AUDIENCE_MISMATCH:
    "名额组织必须与受众组织完全相同：受众里有的组织都要有一格名额，有名额的都必须在受众里",
  QUOTA_NOT_APPLICABLE: "自主报名的活动不设名额",
  INVALID_QUOTAS: "名额要填成每个组织一个不小于 1 的整数",
  QUOTA_EXHAUSTED: "这个组织的名额已经报满了。名额是硬的，不排候补——谁去由组织自己定",
  NOT_QUOTA_ACTIVITY: "这不是分名额的活动，没有报送这一步",
  NOT_IN_QUOTA_ORGANIZATION: "这个人不是有名额的组织的在册会员",
  ALREADY_NOMINATED: "这个人已经报上去了；要换人先撤回",
  NOT_NOMINATED: "这个人不在已报送名单里",
  ENROLLMENT_BY_ORGANIZATION: "这场活动由所在组织报送名单，不能自己报名或退出；去不了可以请假",
  ACTIVITY_NOT_OPEN: "活动还没有发布，或已经取消",
  ENROLLMENT_WINDOW_CLOSED: "已经过了报送时间",
  ON_SITE_CODE_OFF: "这一场没有用现场口令签到",
  INVALID_CHECK_IN_MODE: "签到方式不对，请刷新后重试",
  // 账号与口令（WO-44）
  WEAK_PASSWORD: "口令至少 12 位，且要同时含大写字母、小写字母与数字",
  PASSWORD_UNCHANGED: "新口令不能与当前口令相同",
  IDENTITY_ALREADY_ACTIVE: "这个登录名已经被占用了",
  ACCOUNT_INACTIVE: "这个账号已停用",
  INVALID_REQUEST: "填写的内容不完整或不合规"
};

export function reasonText(error: unknown): string {
  if (error instanceof ApiError) {
    if (["RATE_LIMITED", "LOGIN_LOCKED"].includes(error.reasonCode) && error.retryAfterSeconds) {
      return `请求较频繁，请等待 ${error.retryAfterSeconds} 秒后重试。`;
    }
    return REASON_TEXT[error.reasonCode] ?? `未完成（${error.reasonCode}）`;
  }
  return REASON_TEXT.SERVICE_UNAVAILABLE;
}

export type AttachmentSummary = {
  id: string;
  version?: number;
  fileName: string;
  contentType: string;
  byteSize: number;
  contentHash: string;
  status: "ACTIVE" | "QUARANTINED" | "SUPERSEDED";
  quarantineReason?: string;
  uploadedAt: string;
};

// 附件以原始字节上传：类型由服务端嗅探字节头决定，这里声称什么都不作数。
// 文件名走请求头（base64url 承载 UTF-8），不进查询串——查询串会被访问日志记下。
export async function uploadAttachment(input: {
  ownerType: "CLUE_SUBMISSION" | "PORTAL_CONTENT" | "VERIFICATION_TASK" | "ACTIVITY" | "TOPIC_MANUSCRIPT_VERSION" | "DATA_QUALITY_ISSUE";
  ownerId: string;
  file: File;
}): Promise<AttachmentSummary> {
  const query = new URLSearchParams({
    ownerType: input.ownerType,
    ownerId: input.ownerId,
    commandId: commandId()
  });
  const encodedName = btoa(String.fromCharCode(...new TextEncoder().encode(input.file.name)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
  const response = await fetch(`/api/v1/attachments?${query.toString()}`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/octet-stream",
      "X-Attachment-Filename": encodedName
    },
    body: await input.file.arrayBuffer()
  });
  const data = (await response.json().catch(() => null)) as { reasonCode?: string } | null;
  if (!response.ok) {
    throw new ApiError(response.status, data?.reasonCode ?? "REQUEST_REJECTED");
  }
  return data as unknown as AttachmentSummary;
}

export function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
